/**
 * The Keycloak redirect target, `${origin}/auth/callback` (src/auth/session.ts).
 *
 * G2 §2.6 — "Empty, loading, error and no-permission states designed for every surface —
 * never a blank flash." A code exchange is no exception: it renders a waiting state while
 * the exchange is in flight and a failure state if it does not complete.
 *
 * Both render through `SignInSurface` (ADR 0086), which is the same surface `LoginGate`
 * renders. That is the point: this screen is the second half of a sign-in that began on
 * the first, and a planner who pressed "Sign in" under a photograph and came back to a
 * grey skeleton has been shown a seam that is not there. Nothing extra is fetched for
 * it — the hero is the same five renditions, already in cache.
 *
 * On success there is no route table entry to hand this back to — `App.tsx` renders this
 * component standalone, outside `AppChrome` and `LoginGate` (§2.6's own gate would refuse
 * the very screen that signs a visitor in). So navigating away is done directly against
 * the History API via `writeUrl` — the same function `useUrlState` (router.tsx) calls —
 * with `replace: true` so a back-button press never lands back on `/auth/callback`, then
 * a `popstate` event is dispatched by hand to resync `useUrlState`'s listener. This is
 * the one legitimate use of that trick: `useUrlState` only resyncs from `popstate`, and a
 * synthetic `replaceState` never fires one on its own. It is not a second URL-state
 * mechanism — it drives the same `writeUrl`/`readUrl`/`popstate` channel `router.tsx`
 * already owns.
 *
 * The same callback completes "Change password" and "Edit personal data" (bata-ui#132 round 2): Keycloak finishes the
 * `UPDATE_PASSWORD` action with an ordinary authorization code plus `kc_action_status`. A
 * `cancelled` change goes straight back, as a sign-in does — nothing happened and nothing needs
 * saying. `success` and `error` stop here and SAY so, with one press back to the page: going back
 * silently after an `error` would leave the planner believing a password changed that did not,
 * and after a `success` the one thing the planner needs to know would go unsaid. This
 * application has no toast to carry it, and this surface already exists for exactly this
 * moment, so it is said here.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleAuthCallback, login, startedKeycloakAction } from '../../auth/session';
import { readKeycloakActionStatus, type KeycloakAction } from '../../auth/oidc';
import { SignInPanel, SignInSurface } from '../chrome/SignInSurface';
import { Button, Skeleton } from '../../primitives';
import { writeUrl } from './router';

/** What each action's outcome is called. A status with no recorded action is a password
 *  change: the only action this application started before the Profile screen existed. */
const OUTCOME_KEYS: Readonly<Record<KeycloakAction, {
  readonly changedTitle: string; readonly changedBody: string;
  readonly failedTitle: string; readonly failedBody: string;
}>> = {
  UPDATE_PASSWORD: {
    changedTitle: 'auth.password.changed.title', changedBody: 'auth.password.changed.body',
    failedTitle: 'auth.password.failed.title', failedBody: 'auth.password.failed.body',
  },
  UPDATE_PROFILE: {
    changedTitle: 'auth.profile.changed.title', changedBody: 'auth.profile.changed.body',
    failedTitle: 'auth.profile.failed.title', failedBody: 'auth.profile.failed.body',
  },
  // `success` here is "the request was accepted": the address changes only when the link
  // Keycloak mailed to the NEW address is opened (contract C5). The words say so.
  UPDATE_EMAIL: {
    changedTitle: 'auth.email.requested.title', changedBody: 'auth.email.requested.body',
    failedTitle: 'auth.email.failed.title', failedBody: 'auth.email.failed.body',
  },
};

export function AuthCallback(): React.JSX.Element {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  /** An action Keycloak reported on, and where to go back to once it is read. */
  const [outcome, setOutcome] = useState<{ status: 'success' | 'error'; action: KeycloakAction; path: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Read before the exchange: once `handleAuthCallback` resolves, the URL is about to go.
    const actionStatus = readKeycloakActionStatus(window.location.search);
    const action = startedKeycloakAction() ?? 'UPDATE_PASSWORD';
    handleAuthCallback()
      .then((path) => {
        if (cancelled) return;
        if (actionStatus === 'success' || actionStatus === 'error') {
          setOutcome({ status: actionStatus, action, path });
          return;
        }
        returnTo(path);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (outcome !== null) {
    const ok = outcome.status === 'success';
    const keys = OUTCOME_KEYS[outcome.action];
    return (
      <SignInSurface labelKey="auth.landmark">
        <SignInPanel
          alert={!ok}
          titleKey={ok ? keys.changedTitle : keys.failedTitle}
          bodyKey={ok ? keys.changedBody : keys.failedBody}
        >
          <Button variant="primary" onClick={() => returnTo(outcome.path)}>{t('auth.password.continue')}</Button>
        </SignInPanel>
      </SignInSurface>
    );
  }

  if (failed) {
    // The retry available here is a fresh sign-in attempt, not a refetch — the code
    // exchange itself cannot be retried once Keycloak has consumed the authorization code.
    return (
      <SignInSurface labelKey="auth.landmark">
        <SignInPanel alert titleKey="auth.callback.failed.title" bodyKey="auth.callback.failed.body">
          <Button variant="primary" onClick={() => login()}>{t('action.retry')}</Button>
        </SignInPanel>
      </SignInSurface>
    );
  }

  return (
    <SignInSurface labelKey="auth.landmark">
      <SignInPanel titleKey="auth.callback.title" bodyKey="auth.callback.body">
        <Skeleton rows={3} />
      </SignInPanel>
    </SignInSurface>
  );
}

/** Back to the page the round trip started from, with no `/auth/callback` left in history. */
function returnTo(path: string): void {
  writeUrl({ path, params: {} }, /* replace */ true);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
